import { MatrixEvent, RoomEvent } from 'matrix-js-sdk/src/matrix';
import { RoomStateEvent } from 'matrix-js-sdk/src/models/room-state';
import { useCallback, useEffect, useRef, useState } from 'react';

import { MeetingResponse } from 'api/meedioBackend/types';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useMeetingsScheduleContext from 'contexts/MeetingsScheduleProvider/useMeetingsScheduleContext';
import { CustomMeedioMatrixEvents } from 'utils/matrixUtils';

interface UseScheduledMeetingsResult {
  meetings: MeetingResponse[];
  isLoading: boolean;
  error: unknown;
  refetch: () => Promise<void>;
}

const EMPTY_MEETINGS: MeetingResponse[] = [];

// Reads the per-room scheduled-meeting slice from MeetingsScheduleProvider. The
// bulk store is hydrated on Matrix sync by ScheduledMeetingsWatcher, so this
// hook does not fetch on mount (AC-B7): it returns the room's slice and only
// triggers a single-room refetch when this chat sees a relevant Matrix event.
const useScheduledMeetings = (messengerRoomId: string | null): UseScheduledMeetingsResult => {
  const { matrixClient } = useMatrixContext();
  const {
    getMeetingsForMessengerRoom,
    refetchMessengerRoom,
    isLoading: contextIsLoading,
    error,
  } = useMeetingsScheduleContext();

  const [meetings, setMeetings] = useState<MeetingResponse[]>(EMPTY_MEETINGS);
  // BUG-002 / BUG-018: only push a new array reference to consumers when the
  // serialized slice actually changes. The provider returns a fresh filtered
  // array on every store update; without this guard each update would re-run
  // downstream effects (useScheduledMeetingTriggers) and re-announce every
  // in-window meeting even when this room's payload is unchanged.
  const prevSignatureRef = useRef<string>('');

  useEffect(() => {
    const slice = messengerRoomId ? getMeetingsForMessengerRoom(messengerRoomId) : EMPTY_MEETINGS;
    const signature = JSON.stringify(slice);
    if (signature !== prevSignatureRef.current) {
      prevSignatureRef.current = signature;
      setMeetings(slice);
    }
  }, [messengerRoomId, getMeetingsForMessengerRoom]);

  const refetch = useCallback(async () => {
    if (!messengerRoomId) return;
    await refetchMessengerRoom(messengerRoomId);
  }, [messengerRoomId, refetchMessengerRoom]);

  useEffect(() => {
    if (!messengerRoomId) return undefined;
    const room = matrixClient.getRoom(messengerRoomId);
    if (!room) return undefined;

    const handleTimeline = (event: { getType: () => string; getRoomId: () => string | undefined }) => {
      const type = event.getType();
      if (event.getRoomId() !== messengerRoomId) return;
      if (
        type === CustomMeedioMatrixEvents.SCHEDULED_MEETING ||
        type === CustomMeedioMatrixEvents.MEETING_STARTED ||
        type === CustomMeedioMatrixEvents.MEETING_ENDED
      ) {
        refetchMessengerRoom(messengerRoomId);
      }
    };

    const handleCallState = (event: MatrixEvent) => {
      if (event.getRoomId() !== messengerRoomId) return;
      if (event.getType() !== CustomMeedioMatrixEvents.MESSENGER_CALL) return;
      refetchMessengerRoom(messengerRoomId);
    };

    room.on(RoomEvent.Timeline, handleTimeline);
    room.on(RoomStateEvent.Events, handleCallState);
    return () => {
      room.off(RoomEvent.Timeline, handleTimeline);
      room.off(RoomStateEvent.Events, handleCallState);
    };
  }, [matrixClient, messengerRoomId, refetchMessengerRoom]);

  return {
    meetings,
    isLoading: messengerRoomId ? contextIsLoading : false,
    error,
    refetch,
  };
};

export default useScheduledMeetings;
